/**
 * Columns Service - Fetches column metadata for tables
 * Supports multiple database engines (MSSQL, PostgreSQL)
 */

import type { DatabaseEngineId } from '@/core/engines';
import { executeQueryV2 } from './queryExecutor';
import { createExplorerExecutionId } from './explorerQueryUtils';
import { getQueries } from './queries';

function toBool(value: unknown): boolean {
  return value === 1 || value === '1' || value === true || value === 't';
}

function toNullableNumber(value: unknown): number | null {
  if (value === null || value === undefined) return null;
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function toNullableString(value: unknown): string | null {
  if (value === null || value === undefined) return null;
  return String(value);
}

function getRowValue(row: Record<string, unknown>, keys: string[]): unknown {
  for (const key of keys) {
    const value = row[key];
    if (value !== undefined) return value;
  }
  return undefined;
}

export function mapColumnInfoRows(rows: Record<string, unknown>[]): ColumnInfo[] {
  return rows.map((row) => {
    const name = String(getRowValue(row, ['column_name', 'name']) ?? '');
    const dataTypeRaw = String(getRowValue(row, ['data_type', 'type']) ?? '').trim();
    const dataType = dataTypeRaw.length > 0 ? dataTypeRaw : 'unknown';

    const maxLength = toNullableNumber(getRowValue(row, ['max_length']));
    const precision = toNullableNumber(getRowValue(row, ['precision']));
    const scale = toNullableNumber(getRowValue(row, ['scale']));

    const isNullable =
      row.is_nullable !== undefined
        ? toBool(row.is_nullable)
        : row.notnull !== undefined
          ? !toBool(row.notnull)
          : true;

    const isPrimaryKey =
      row.is_primary_key !== undefined ? toBool(row.is_primary_key) : toBool(row.pk);

    const isIdentity =
      row.is_identity !== undefined ? toBool(row.is_identity) : false;

    const defaultValue = toNullableString(getRowValue(row, ['default_value', 'dflt_value']));

    const ordinalPositionRaw = getRowValue(row, ['ordinal_position', 'cid']);
    const ordinalPosition = toNullableNumber(ordinalPositionRaw) ?? 0;

    return {
      name,
      dataType,
      maxLength,
      precision,
      scale,
      isNullable,
      isPrimaryKey,
      isIdentity,
      defaultValue,
      ordinalPosition,
    };
  });
}

/**
 * Column information returned from database
 */
export interface ColumnInfo {
  name: string;
  dataType: string;
  maxLength: number | null;
  precision: number | null;
  scale: number | null;
  isNullable: boolean;
  isPrimaryKey: boolean;
  isIdentity: boolean;
  defaultValue: string | null;
  ordinalPosition: number;
}

/**
 * Get columns for a specific table
 *
 * @param connectionId The ID of the connection to use
 * @param database The database name
 * @param schema The schema name
 * @param tableName The table name
 * @param engine Database engine type (defaults to 'mssql')
 * @returns Promise with list of column information
 */
export async function getColumns(
  connectionId: string,
  database: string,
  schema: string,
  tableName: string,
  engine?: DatabaseEngineId | string
): Promise<{ columns: ColumnInfo[]; error?: string }> {
  const queries = getQueries(engine);

  try {
    const result = await executeQueryV2({
      connectionId,
      sql: queries.columns(schema, tableName),
      database,
      executionId: createExplorerExecutionId(connectionId),
    });

    if (!result.success) {
      const errorMsg = result.error?.message || 'Unknown error';
      console.error(`[ColumnsService] Error getting columns: ${errorMsg}`);
      return { columns: [], error: errorMsg };
    }

    const rows = result.resultSets?.[0]?.rows || [];
    const columns = mapColumnInfoRows(rows);

    return { columns };
  } catch (error) {
    console.error('[ColumnsService] Error in getColumns:', error);
    return {
      columns: [],
      error: error instanceof Error ? error.message : 'Unknown error getting columns'
    };
  }
}
